import {
  stampAreaToWorkspaceBounds,
  STAMP_DISPLAY_CONTAINER,
} from '@/image-designer/adapters/stampAreasAdapter';

/** 与 ImageTemplateEditorPage 轮播展示区域一致的容器基准 */
export const IMAGE_TEMPLATE_DISPLAY_BASE_WIDTH = 675;
export const IMAGE_TEMPLATE_DISPLAY_BASE_HEIGHT = 900;

/**
 * 计算编辑页展示容器尺寸（与 image-editor-display-image-container 内联尺寸一致）
 */
export function getImageTemplateDisplayContainerSize(imageWidth, imageHeight) {
  const iw = Number(imageWidth) || 0;
  const ih = Number(imageHeight) || 0;
  const baseW = IMAGE_TEMPLATE_DISPLAY_BASE_WIDTH;
  const baseH = IMAGE_TEMPLATE_DISPLAY_BASE_HEIGHT;
  if (!iw || !ih) {
    return { width: baseW, height: baseH };
  }
  const imageAspect = iw / ih;
  const baseAspect = baseW / baseH;
  let width = baseW;
  let height = baseH;
  if (Math.abs(imageAspect - baseAspect) > 0.01) {
    if (imageAspect > baseAspect) {
      height = baseH;
      width = baseH * imageAspect;
    } else {
      width = baseW;
      height = baseW / imageAspect;
    }
  }
  return { width, height };
}

/** 列表/卡片预览框：在父容器内等比 contain（宽或高贴满、不溢出） */
export function fitDisplayBoxInRect(rectWidth, rectHeight, displayBox) {
  const rw = Math.max(1, Number(rectWidth) || 1);
  const rh = Math.max(1, Number(rectHeight) || 1);
  const dw = Number(displayBox?.width) || IMAGE_TEMPLATE_DISPLAY_BASE_WIDTH;
  const dh = Number(displayBox?.height) || IMAGE_TEMPLATE_DISPLAY_BASE_HEIGHT;
  const scale = Math.min(rw / dw, rh / dh);
  return {
    width: dw * scale,
    height: dh * scale,
  };
}

/** 商品模板库卡片 media 内主预览区可用尺寸（与 ipe-workbench.css 250×300 卡片一致） */
export const TEMPLATE_GALLERY_CARD = {
  mediaWidth: 250,
  mediaHeight: 300,
  comboPadX: 6,
  comboPadY: 4,
  sideColumn: 55,
};

export function getTemplateGalleryMainStageSize(hasSideSlides) {
  const w = TEMPLATE_GALLERY_CARD.mediaWidth
    - TEMPLATE_GALLERY_CARD.comboPadX
    - (hasSideSlides ? TEMPLATE_GALLERY_CARD.sideColumn : 0);
  const h = TEMPLATE_GALLERY_CARD.mediaHeight - TEMPLATE_GALLERY_CARD.comboPadY;
  return { width: Math.max(1, w), height: Math.max(1, h) };
}

export function getTemplateGallerySideStageSize(sideCount) {
  const count = Math.max(1, Number(sideCount) || 1);
  const w = TEMPLATE_GALLERY_CARD.sideColumn - 2;
  const h = (TEMPLATE_GALLERY_CARD.mediaHeight - TEMPLATE_GALLERY_CARD.comboPadY - (count - 1) * 4) / count;
  return { width: Math.max(1, w), height: Math.max(1, h) };
}

export function getImageTemplatePreviewFrameStyle(displayBox, fittedSize) {
  const width = Number(displayBox?.width) || IMAGE_TEMPLATE_DISPLAY_BASE_WIDTH;
  const height = Number(displayBox?.height) || IMAGE_TEMPLATE_DISPLAY_BASE_HEIGHT;
  const style = {
    aspectRatio: `${width} / ${height}`,
    '--tpl-preview-ar-w': width,
    '--tpl-preview-ar-h': height,
    position: 'relative',
    flexShrink: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
  };
  if (fittedSize && fittedSize.width > 0 && fittedSize.height > 0) {
    style.width = `${fittedSize.width}px`;
    style.height = `${fittedSize.height}px`;
    style.maxWidth = '100%';
    style.maxHeight = '100%';
  }
  return style;
}

/**
 * 列表卡片预览框比例：优先模板 workspace / 轮播图元数据，不用 PNG 实测尺寸（避免方形导出图把框算成正方形）。
 */
export function resolveTemplateCardPreviewDisplayBox(template, { refW = 0, refH = 0 } = {}) {
  const td = template?.templateData && typeof template.templateData === 'object' ? template.templateData : {};
  const wsW = Number(td.workspaceWidth) || 0;
  const wsH = Number(td.workspaceHeight) || 0;
  const iw = wsW || Number(refW) || IMAGE_TEMPLATE_DISPLAY_BASE_WIDTH;
  const ih = wsH || Number(refH) || IMAGE_TEMPLATE_DISPLAY_BASE_HEIGHT;
  return getImageTemplateDisplayContainerSize(iw, ih);
}

/**
 * 印花框在展示容器内的百分比定位（stamp 已是展示容器像素坐标时用）
 * @deprecated 列表预览请用 stampOverlayStyleOnPreviewImage（stampAreas 为 900 contain 坐标）
 */
export function stampOverlayStyleOnDisplayContainer(stamp, containerWidth, containerHeight) {
  if (!stamp || typeof stamp !== 'object') return null;
  const cw = Math.max(1, Number(containerWidth) || IMAGE_TEMPLATE_DISPLAY_BASE_WIDTH);
  const ch = Math.max(1, Number(containerHeight) || IMAGE_TEMPLATE_DISPLAY_BASE_HEIGHT);
  let x = Number(stamp.x) || 0;
  let y = Number(stamp.y) || 0;
  let w = Math.max(1, Number(stamp.width) || 1);
  let h = Math.max(1, Number(stamp.height) || 1);
  const rot = Number(stamp.rotation) || 0;
  const base = { transformOrigin: 'center center' };
  const tr = rot ? `rotate(${rot}deg)` : undefined;

  const maxNorm = Math.max(x, y, w, h);
  if (maxNorm > 0 && maxNorm <= 1.0001) {
    return {
      ...base,
      left: `${x * 100}%`,
      top: `${y * 100}%`,
      width: `${w * 100}%`,
      height: `${h * 100}%`,
      transform: tr,
    };
  }

  return {
    ...base,
    left: `${(x / cw) * 100}%`,
    top: `${(y / ch) * 100}%`,
    width: `${(w / cw) * 100}%`,
    height: `${(h / ch) * 100}%`,
    transform: tr,
  };
}

/**
 * 列表/缩略图：stampAreas 存的是旧编辑器 900×900 contain 坐标，
 * 先还原为工作区像素再映射到预览图，避免用 675×900 直接除导致横向偏移、放大。
 * 预览框 aspect 与图一致且图片填满时，百分比与产品图上印花位置一致。
 */
export function stampOverlayStyleOnPreviewImage(stamp, options = {}) {
  if (!stamp || typeof stamp !== 'object') return null;

  let x = Number(stamp.x) || 0;
  let y = Number(stamp.y) || 0;
  let w = Math.max(1, Number(stamp.width) || 1);
  let h = Math.max(1, Number(stamp.height) || 1);
  const rot = Number(stamp.rotation) || 0;
  const base = { transformOrigin: 'center center' };
  const tr = rot ? `rotate(${rot}deg)` : undefined;

  const maxNorm = Math.max(Math.abs(x), Math.abs(y), w, h);
  if (maxNorm > 0 && maxNorm <= 1.0001) {
    return {
      ...base,
      left: `${x * 100}%`,
      top: `${y * 100}%`,
      width: `${w * 100}%`,
      height: `${h * 100}%`,
      transform: tr,
    };
  }

  const imgW = Math.max(
    1,
    Number(options.imageWidth) || Number(options.workspaceWidth) || STAMP_DISPLAY_CONTAINER,
  );
  const imgH = Math.max(
    1,
    Number(options.imageHeight) || Number(options.workspaceHeight) || STAMP_DISPLAY_CONTAINER,
  );
  const wsW = Math.max(1, Number(options.workspaceWidth) || imgW);
  const wsH = Math.max(1, Number(options.workspaceHeight) || imgH);

  const bounds = stampAreaToWorkspaceBounds(
    { x, y, width: w, height: h, rotation: rot },
    { width: wsW, height: wsH },
  );
  const scaleX = imgW / wsW;
  const scaleY = imgH / wsH;
  const px = bounds.left * scaleX;
  const py = bounds.top * scaleY;
  const pw = Math.max(1, bounds.width * scaleX);
  const ph = Math.max(1, bounds.height * scaleY);

  return {
    ...base,
    left: `${(px / imgW) * 100}%`,
    top: `${(py / imgH) * 100}%`,
    width: `${(pw / imgW) * 100}%`,
    height: `${(ph / imgH) * 100}%`,
    transform: tr,
  };
}
